import type { ReactNode } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '../../../../lib/utils'
import type { Claim } from '../../../../models/claim'
import {
  CITATION_LABELS,
  DRIVEABLE_LABELS,
  DRIVER_RELATIONSHIP_LABELS,
  formatDriver,
  formatIncidentDate,
  formatIncidentTime,
  formatOtherInsurance,
  formatReporterRelationships,
  formatTrailer,
  formatTruck,
  HOW_MANY_VEHICLES_LABELS,
  INJURED_PARTY_LABELS,
  joinLabels,
  LOAD_STATUS_LABELS,
  TOW_LABELS,
  WHAT_DAMAGED_LABELS,
  WHAT_HAPPENED_LABELS,
} from './claimSummary'

type Tone = 'default' | 'supplemental' | 'positive' | 'alert'

function Pill({ tone, children }: { tone: 'positive' | 'alert'; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
        tone === 'positive' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
      )}
    >
      {children}
    </span>
  )
}

function Row({ label, value, tone = 'default' }: { label: string; value: ReactNode; tone?: Tone }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-neutral-100 py-2 last:border-b-0">
      <span className="text-sm text-neutral-500">{label}</span>
      <span className="text-right">
        {tone === 'positive' || tone === 'alert' ? (
          <Pill tone={tone}>{value}</Pill>
        ) : (
          <span
            className={cn(
              'text-sm font-medium text-neutral-900',
              tone === 'supplemental' && 'font-normal text-neutral-400 italic'
            )}
          >
            {value}
          </span>
        )}
      </span>
    </div>
  )
}

/** Free text needs the full width; the Row layout right-aligns short values. */
function Passage({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 border-b border-neutral-100 py-2 last:border-b-0">
      <span className="text-sm text-neutral-500">{label}</span>
      <p className="text-sm font-medium whitespace-pre-line text-neutral-900">{value}</p>
    </div>
  )
}

function SummaryCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">{children}</CardContent>
    </Card>
  )
}

interface ClaimReviewSummaryProps {
  claim: Claim
  orgName: string
  /** Every in-force policy number — an org can hold one per coverage line. */
  policyIds: string[]
}

export function ClaimReviewSummary({ claim, orgName, policyIds }: ClaimReviewSummaryProps) {
  const i = claim.incident
  const inj = claim.injuries
  const pol = claim.police
  const numInjured = inj?.numInjured ?? null

  return (
    <>
      <SummaryCard title="Policy & driver">
        <Row label={policyIds.length > 1 ? 'Policies' : 'Policy'} value={policyIds.join(', ') || '—'} />
        <Row label="Insured" value={orgName || '—'} />
        <Row label="Truck" value={formatTruck(claim.truck)} />
        <Row label="Trailer attached" value={formatTrailer(claim.trailerAttached, claim.trailer)} />
        <Row label="Driver" value={formatDriver(claim.driver)} />
        <Row
          label="Driver relationship"
          value={(claim.driver && DRIVER_RELATIONSHIP_LABELS[claim.driver.relationshipToInsured]) || '—'}
        />
        <Row
          label="On duty at the time"
          value={claim.driver?.wasOnDuty == null ? '—' : claim.driver.wasOnDuty ? 'Yes' : 'No'}
        />
      </SummaryCard>

      <SummaryCard title="Incident">
        <Row
          label="Date & time"
          value={
            [formatIncidentDate(i?.date ?? null), formatIncidentTime(i?.time ?? null)]
              .filter(Boolean)
              .join(' · ') || '—'
          }
        />
        <Row
          label="Location"
          value={
            [[i?.city, i?.state].filter(Boolean).join(', '), i?.locationDescription]
              .filter(Boolean)
              .join(' · ') || '—'
          }
        />
        <Row label="What happened" value={joinLabels(i?.whatHappened ?? null, WHAT_HAPPENED_LABELS) || '—'} />
        <Row label="Vehicles involved" value={(i && HOW_MANY_VEHICLES_LABELS[i.howManyVehicles]) || '—'} />
        <Row
          label="What was damaged"
          value={joinLabels(i?.whatDamaged ?? null, WHAT_DAMAGED_LABELS) || '—'}
        />
        <Row
          label="Cargo on board"
          value={i?.cargoOnBoard == null ? '—' : i.cargoOnBoard ? 'Yes' : 'No — running empty'}
        />
        <Row label="Freight broker" value={i?.freightBrokerName || '—'} />
        <Row label="Broker contact" value={i?.freightBrokerContact || '—'} />
        <Row label="Load" value={(i && LOAD_STATUS_LABELS[i.loadStatus]) || '—'} />
        <Row label="Truck driveable" value={(i && DRIVEABLE_LABELS[i.truckDriveable]) || '—'} />
        <Row label="Needs a tow" value={joinLabels(i?.vehiclesNeedingTow ?? null, TOW_LABELS) || '—'} />
        {i?.briefDescription && <Passage label="In their words" value={i.briefDescription} />}
        {i?.anythingElse && <Passage label="Anything else" value={i.anythingElse} />}
        <Row label="Coverage triggered" value="Determined by broker on review" tone="supplemental" />
      </SummaryCard>

      <SummaryCard title="Others involved">
        {/* Defaults to 0 and phone intake never asks, so 0 can mean nobody asked. */}
        {inj && inj.numPassengers > 0 && <Row label="Passengers in our vehicle" value={inj.numPassengers} />}
        <Row
          label="Injuries"
          value={
            inj?.anyInjuries
              ? `Yes — ${numInjured ?? '?'} ${numInjured === 1 ? 'person' : 'people'}`
              : 'No injuries'
          }
          tone={inj?.anyInjuries ? 'alert' : 'default'}
        />
        {inj?.anyInjuries && (
          <Row
            label="Who was injured"
            value={joinLabels(inj.injuredParties ?? null, INJURED_PARTY_LABELS) || '—'}
          />
        )}
        {inj?.anyInjuries && (
          <Row
            label="Hospital transport"
            value={inj.transportedToHospital ? 'Yes' : 'No'}
            tone={inj.transportedToHospital ? 'alert' : 'default'}
          />
        )}
        {claim.otherVehiclesInvolved && (
          <>
            <Row label="Other vehicle plate" value={claim.otherVehicle?.licensePlate || '—'} />
            <Row label="Other driver" value={claim.otherVehicle?.otherDriverName || '—'} />
            <Row label="Their insurance" value={formatOtherInsurance(claim.otherVehicle)} />
            <Row label="Their contact" value={claim.otherVehicle?.contactInfo || '—'} />
          </>
        )}
        <Row
          label="Police contacted"
          value={pol?.policeContacted ? (pol.policeResponded ? 'Yes — responded' : 'Yes') : 'No'}
          tone={pol?.policeContacted ? 'positive' : 'default'}
        />
        {pol?.policeContacted && (
          <>
            <Row
              label="Report filed"
              value={pol.reportFiled ? 'Yes' : 'No'}
              tone={pol.reportFiled ? 'positive' : 'default'}
            />
            <Row
              label="Report number"
              value={pol.reportCaseNumber || 'Supplemental'}
              tone={pol.reportCaseNumber ? 'default' : 'supplemental'}
            />
            <Row label="Jurisdiction" value={pol.jurisdiction || '—'} />
          </>
        )}
        <Row label="Citation" value={(pol && CITATION_LABELS[pol.citationIssued]) || '—'} />
      </SummaryCard>

      <SummaryCard title="Photos & documents">
        <Row
          label="Files submitted"
          value={
            claim.photos.length > 0
              ? `✓ ${claim.photos.length} ${claim.photos.length === 1 ? 'file' : 'files'}`
              : claim.photoUploadDeferred
                ? 'Deferred'
                : 'None'
          }
          tone={claim.photos.length > 0 ? 'positive' : 'default'}
        />
        {claim.photoUploadDeferred && claim.photoDeferralReason && claim.photos.length === 0 && (
          <Row label="Why they were deferred" value={claim.photoDeferralReason} />
        )}
        {claim.photos.length > 0 && (
          <ul className="flex flex-col gap-0.5 pt-3">
            {claim.photos.map((p) => (
              <li key={p.id} className="text-sm text-neutral-800">
                {p.fileUrl ? (
                  <a href={p.fileUrl} target="_blank" rel="noreferrer" className="hover:underline">
                    {p.fileName}
                  </a>
                ) : (
                  p.fileName
                )}
              </li>
            ))}
          </ul>
        )}
      </SummaryCard>

      <SummaryCard title="Reported by">
        <Row
          label="Reporter"
          value={[claim.reporter?.name, claim.reporter?.phone].filter(Boolean).join(' · ') || '—'}
        />
        <Row
          label="Relationship"
          value={formatReporterRelationships(claim.reporter?.relationships ?? []) || '—'}
        />
      </SummaryCard>
    </>
  )
}
